import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { JobEntityType, PatientEntityType } from "@/lib/supabase/supabaseTypes";
import { ArrowLeft, CheckCircle, AlertTriangle } from "lucide-react";
import { usePatientStore } from "@/dentlab/src/store/Patient";
import { PatientSelector } from "./components/patient-selector.component";
import { PatientJobsList } from "./components/patient-jobs-list.component";
import { KeeperPatientPanel } from "./components/keeper-patient-panel.component";
import { MergeOverview } from "./components/merge-overview.component";

export const PatientMergePage: React.FC = () => {
	const { patientId } = useParams<{ patientId: string }>();
	// Remount on keeper change so every piece of merge state (selection,
	// confirmation, loaded jobs) starts fresh for the new staying patient.
	return <PatientMergeContent key={patientId} patientId={patientId} />;
};

interface PatientMergeContentProps {
	patientId: string | undefined;
}

const PatientMergeContent: React.FC<PatientMergeContentProps> = ({
	patientId,
}) => {
	const navigate = useNavigate();
	const [keeperPatient, setKeeperPatient] =
		useState<PatientEntityType | null>(null);
	const [selectedDuplicates, setSelectedDuplicates] = useState<
		PatientEntityType[]
	>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [mergedCount, setMergedCount] = useState(0);
	const [mergeSuccess, setMergeSuccess] = useState(false);
	// Ids selected via the manual free search rather than the duplicate suggestions.
	// These are arbitrary merges and demand an explicit "same person" confirmation.
	const [manualIds, setManualIds] = useState<Set<string>>(new Set());
	// Bumped after a detach so the job lists refetch.
	const [refreshToken, setRefreshToken] = useState(0);
	// Jobs of keeper + selected patients, loaded when entering the confirmation.
	const [jobsByPatient, setJobsByPatient] = useState<
		Record<string, JobEntityType[]>
	>({});

	const {
		patientName,
		getPatient,
		mergePatients,
		removePatientFromLookup,
		fetchPatientJobsMap,
	} = usePatientStore((state) => ({
		patientName: state.patientName,
		getPatient: state.getPatient,
		mergePatients: state.mergePatients,
		removePatientFromLookup: state.removePatientFromLookup,
		fetchPatientJobsMap: state.fetchPatientJobsMap,
	}));

	useEffect(() => {
		if (!patientId) return;
		let cancelled = false;
		// Always hydrate the full row: a cached LazyPatient lacks birth_date, which
		// would silently kill the birth-date matching and the conflict warning.
		getPatient(patientId).then((patient) => {
			if (!cancelled && patient) setKeeperPatient(patient);
		});
		return () => {
			cancelled = true;
		};
	}, [patientId, getPatient, refreshToken]);

	const handleTogglePatient = (
		candidate: PatientEntityType,
		isManual = false
	) => {
		const wasSelected = selectedDuplicates.some(
			(p) => p.id === candidate.id
		);
		setSelectedDuplicates((prev) =>
			wasSelected
				? prev.filter((p) => p.id !== candidate.id)
				: [...prev, candidate]
		);
		setManualIds((prev) => {
			const next = new Set(prev);
			if (wasSelected) {
				next.delete(candidate.id);
			} else if (isManual) {
				next.add(candidate.id);
			}
			return next;
		});
	};

	const handleContinue = async () => {
		if (!keeperPatient) return;
		setIsLoading(true);
		setJobsByPatient(
			await fetchPatientJobsMap([
				keeperPatient.id,
				...selectedDuplicates.map((p) => p.id),
			])
		);
		setIsLoading(false);
		setShowConfirmation(true);
	};

	const handleDataChanged = () => {
		if (patientId) removePatientFromLookup(patientId);
		setRefreshToken((n) => n + 1);
	};

	const handleMergePatients = async () => {
		if (!keeperPatient?.id || selectedDuplicates.length === 0) return;

		setIsLoading(true);
		const ok = await mergePatients(
			keeperPatient.id,
			selectedDuplicates.map((p) => p.id)
		);
		setIsLoading(false);
		if (ok) {
			setMergedCount(selectedDuplicates.length);
			setMergeSuccess(true);
		}
	};

	const formatBirthDate = (birthDate: string | null) => {
		if (!birthDate) return "";
		return new Date(birthDate).toLocaleDateString("de-DE");
	};

	const selectedIds = selectedDuplicates.map((p) => p.id);

	if (mergeSuccess && keeperPatient) {
		return (
			<div className="min-h-screen bg-gray-50 py-8">
				<div className="max-w-3xl mx-auto px-4">
					<div className="bg-white rounded-lg shadow">
						<div className="p-6 border-b text-center">
							<CheckCircle className="w-16 h-16 text-green-500 mx-auto my-4" />
							<h1 className="text-2xl font-bold text-gray-900 mb-2">
								{mergedCount === 1
									? "Patienten erfolgreich zusammengelegt"
									: `${mergedCount} Patienten erfolgreich zusammengelegt`}
							</h1>
						</div>

						<div className="p-6 space-y-6">
							<div className="bg-green-50 border border-green-200 rounded p-4">
								<h2 className="font-semibold text-green-800 mb-3">
									Zusammengeführter Patient
								</h2>
								<div className="text-gray-900">
									<p className="font-medium text-lg">
										{patientName(keeperPatient)}
									</p>
									<div className="text-sm text-gray-600 mt-1 space-y-0.5">
										{keeperPatient.code && (
											<div>
												Code: {keeperPatient.code}
											</div>
										)}
										{keeperPatient.birth_date && (
											<div>
												Geb.:{" "}
												{formatBirthDate(
													keeperPatient.birth_date
												)}
											</div>
										)}
									</div>
								</div>
							</div>

							<div>
								<h3 className="font-semibold text-gray-900 mb-3">
									Alle Aufträge des Patienten
								</h3>
								<PatientJobsList patientId={keeperPatient.id} />
							</div>

							<div className="pt-4 border-t flex justify-center">
								<button
									onClick={() => navigate("/")}
									className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
								>
									Zum Dashboard
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (showConfirmation && keeperPatient && selectedDuplicates.length > 0) {
		const conflictingDuplicates = selectedDuplicates.filter(
			(p) =>
				!!p.birth_date &&
				!!keeperPatient.birth_date &&
				p.birth_date !== keeperPatient.birth_date
		);
		const hasIdentityConflict = conflictingDuplicates.length > 0;
		const incomingCount = selectedDuplicates.reduce(
			(sum, p) => sum + (jobsByPatient[p.id]?.length ?? 0),
			0
		);
		return (
			<div className="min-h-screen bg-gray-50 flex flex-col">
				{/* h-16 keeps the sticky identity headers in MergeOverview
				    (top-16) flush below this bar. */}
				<div className="bg-white border-b px-6 h-16 flex items-center sticky top-0 z-10">
					<button
						onClick={() => setShowConfirmation(false)}
						className="mr-4 p-1 rounded hover:bg-gray-100"
						aria-label="Zurück"
					>
						<ArrowLeft className="w-5 h-5" />
					</button>
					<h1 className="text-xl font-semibold text-gray-900">
						Zusammenführung bestätigen
					</h1>
				</div>

				<div className="flex-1 px-6 py-6 space-y-6">
					{hasIdentityConflict && (
						<div className="bg-red-50 border border-red-200 rounded p-4 space-y-2 lg:max-w-xl">
							<h3 className="font-medium text-red-800">
								<AlertTriangle className="w-4 h-4 text-red-800 inline-block mr-2" />
								Unterschiedliche Geburtsdaten – bitte prüfen
							</h3>
							<ul className="text-sm text-red-700 list-disc list-inside space-y-0.5">
								{conflictingDuplicates.map((p) => (
									<li key={p.id}>
										{patientName(p)}:{" "}
										{formatBirthDate(p.birth_date)} ↔{" "}
										{formatBirthDate(
											keeperPatient.birth_date
										)}
									</li>
								))}
							</ul>
						</div>
					)}

					<MergeOverview
						keeperPatient={keeperPatient}
						sources={selectedDuplicates}
						manualIds={manualIds}
						jobsByPatient={jobsByPatient}
						patientName={patientName}
						formatBirthDate={formatBirthDate}
					/>
				</div>

				<div className="sticky bottom-0 bg-white border-t px-6 py-3 flex flex-wrap items-center gap-4">
					<p className="text-sm text-gray-600 flex-1 min-w-64">
						{selectedDuplicates.length === 1
							? "1 Patient wird "
							: `${selectedDuplicates.length} Patienten werden `}
						mit{" "}
						{incomingCount === 1
							? "1 Auftrag"
							: `${incomingCount} Aufträgen`}
						, allen Rechnungen und Freigaben auf{" "}
						<span className="font-medium text-gray-900">
							{patientName(keeperPatient)}
						</span>{" "}
						übertragen. Die Quell-Patienten werden dabei gelöscht —
						diese Aktion kann nicht rückgängig gemacht werden.
					</p>
					<div className="ml-auto flex items-center gap-3">
						<button
							onClick={() => setShowConfirmation(false)}
							className="px-4 py-2 text-base text-gray-600 hover:text-gray-800"
						>
							Abbrechen
						</button>
						<button
							onClick={handleMergePatients}
							disabled={isLoading}
							className="bg-red-600 text-white px-4 py-2 rounded text-base hover:bg-red-700 disabled:opacity-50"
							data-testid="patient-merge-confirm"
						>
							{isLoading
								? "Wird zusammengelegt..."
								: selectedDuplicates.length === 1
									? "Zusammenlegen"
									: `${selectedDuplicates.length} Patienten zusammenlegen`}
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gray-50 flex flex-col">
			<div className="bg-white border-b px-6 py-4 flex items-center sticky top-0 z-10">
				<button
					onClick={() => navigate(-1)}
					className="mr-4 p-1 rounded hover:bg-gray-100"
					aria-label="Zurück"
				>
					<ArrowLeft className="w-5 h-5" />
				</button>
				<h1 className="text-xl font-semibold text-gray-900">
					Patienten zusammenlegen
				</h1>
			</div>

			<div className="flex-1 px-6 py-6">
				{keeperPatient && (
					<div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,26rem)] gap-6 items-start">
						<div className="space-y-4 order-2 xl:order-1">
							<div className="bg-white border rounded-lg p-4">
								<PatientSelector
									keeperPatient={keeperPatient}
									selectedIds={selectedIds}
									onTogglePatient={handleTogglePatient}
								/>
							</div>

							<details className="bg-white border rounded-lg">
								<summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium text-gray-700">
									Auftrag zu anderem Patienten verschieben
								</summary>
								<div className="px-4 pb-4">
									<PatientJobsList
										key={`jobs-${refreshToken}`}
										patientId={keeperPatient.id}
										allowTransfer
										onChanged={handleDataChanged}
									/>
								</div>
							</details>
						</div>

						<div className="bg-white border rounded-lg p-4 order-1 xl:order-2 xl:sticky xl:top-20 flex flex-col xl:max-h-[calc(100vh-7rem)]">
							<div className="flex-1 min-h-0 overflow-y-auto pr-1">
								<KeeperPatientPanel patient={keeperPatient} />
							</div>

							{selectedDuplicates.length > 0 && (
								<div className="flex items-center justify-between pt-4 mt-4 border-t shrink-0">
									<span className="text-sm text-gray-600">
										{selectedDuplicates.length === 1
											? "1 Patient ausgewählt"
											: `${selectedDuplicates.length} Patienten ausgewählt`}
									</span>
									<button
										onClick={handleContinue}
										disabled={isLoading}
										className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
										data-testid="patient-merge-continue"
									>
										{isLoading
											? "Lädt..."
											: "Weiter zur Bestätigung"}
									</button>
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};
